import { z } from "zod";
import { TASK_CATEGORY_SLUGS } from "../constants/task-categories.js";
// ─── Projects ──────────────────────────────────────────────
export const ProjectStatusEnum = z.enum([
    "planning",
    "active",
    "on_hold",
    "completed",
    "cancelled",
]);
export const ProjectWorkKindSchema = z.enum(["project", "retainer"]);
export const ProjectTemplateIdSchema = z.enum([
    "client-kickoff",
    "monthly-retainer",
    "implementation-sprint",
]);
export const StakeholderSchema = z.object({
    email: z.string().email(),
    name: z.string().optional(),
    role: z.string().optional(),
    notifyLevel: z.enum(["all", "milestones", "quarterly"]).default("all"),
});
export const TeamMemberSchema = z.object({
    email: z.string().email(),
    role: z.string().default("contributor"),
});
export const KanbanColumnSchema = z.object({
    id: z.string().min(1).max(50),
    label: z.string().min(1).max(50),
    color: z
        .string()
        .regex(/^#[0-9a-f]{6}$/i)
        .default("#94a3b8"),
});
export const ProjectCreateSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(10_000).optional(),
    workKind: ProjectWorkKindSchema.optional(),
    status: ProjectStatusEnum.default("active"),
    clientName: z.string().max(200).optional(),
    startDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    endDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    budgetAmount: z.number().nonnegative().optional(),
    budgetCurrency: z.string().length(3).default("GBP"),
    stakeholders: z.array(StakeholderSchema).default([]),
    team: z.array(TeamMemberSchema).default([]),
    kanbanColumns: z.array(KanbanColumnSchema).default([
        { id: "backlog", label: "Backlog", color: "#94a3b8" },
        { id: "todo", label: "To do", color: "#f59e0b" },
        { id: "in_progress", label: "In progress", color: "#3b82f6" },
        { id: "blocked", label: "Blocked", color: "#ef4444" },
        { id: "done", label: "Done", color: "#10b981" },
    ]),
    // Project-level toggle controlling whether portal users (magic-link
    // auth) for the same tenant can see a read-only projection of this
    // project. The DB column defaults to false, so a brand-new project
    // is private to staff until they explicitly share. Optional in the
    // schema (no Zod default) because callers who don't care about
    // sharing shouldn't have to pass a value.
    sharedWithPortal: z.boolean().optional(),
    // Optional CRM handoff link. A won deal can create a delivery
    // project exactly once; this field keeps the handoff auditable.
    sourceDealId: z.string().uuid().nullable().optional(),
    // Map-primary overlay (ADR-0111). When omitted, createProject creates a
    // twin ideation map and sets map_id. When provided, links to that map
    // (must be unique — one project per map).
    mapId: z.string().uuid().optional(),
    // Optional starter task template. This shapes initial delivery
    // work only; it is not persisted on the project row.
    templateId: ProjectTemplateIdSchema.optional(),
});
export const ProjectUpdateSchema = ProjectCreateSchema.omit({ templateId: true }).partial();
const QueryBooleanSchema = z.preprocess((value) => {
    if (value === undefined || typeof value === "boolean")
        return value;
    if (typeof value !== "string")
        return value;
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "1")
        return true;
    if (normalized === "false" || normalized === "0")
        return false;
    return value;
}, z.boolean().optional());
export const ProjectSchema = ProjectCreateSchema.omit({ templateId: true }).extend({
    id: z.string().uuid(),
    workKind: ProjectWorkKindSchema.default("project"),
    mapId: z.string().uuid().nullable().default(null),
    createdBy: z.string().email(),
    archivedAt: z.string().datetime().nullable().default(null),
    archivedBy: z.string().email().nullable().default(null),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const ProjectListQuerySchema = z.object({
    // Admin-only opt-ins. Server enforces; non-admins always see active-only.
    includeArchived: QueryBooleanSchema,
    archivedOnly: QueryBooleanSchema,
});
// ─── Discovery briefs ─────────────────────────────────────
export const ProjectDiscoveryBriefStatusSchema = z.enum(["draft", "approved"]);
export const ProjectDiscoveryBriefSourceRefSchema = z.record(z.string(), z.unknown());
export const ProjectDiscoveryBriefSchema = z.object({
    id: z.string().uuid(),
    projectId: z.string().uuid(),
    title: z.string().min(1).max(220),
    body: z.string().min(1).max(12_000),
    status: ProjectDiscoveryBriefStatusSchema,
    sourceRefs: z.array(ProjectDiscoveryBriefSourceRefSchema).default([]),
    approvedAt: z.string().datetime().nullable(),
    approvedBy: z.string().email().nullable(),
    archivedAt: z.string().datetime().nullable(),
    archivedBy: z.string().email().nullable(),
    createdBy: z.string().email(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const ProjectDiscoveryBriefListQuerySchema = z.object({
    includeArchived: QueryBooleanSchema.default(false),
    archivedOnly: QueryBooleanSchema,
});
export const ProjectDiscoveryBriefListResponseSchema = z.object({
    briefs: z.array(ProjectDiscoveryBriefSchema),
});
export const ProjectDiscoveryBriefCreateSchema = z.object({
    title: z.string().trim().min(1).max(220),
    body: z.string().trim().min(1).max(12_000),
    sourceRefs: z.array(ProjectDiscoveryBriefSourceRefSchema).max(20).default([]),
});
export const ProjectDiscoveryBriefUpdateSchema = ProjectDiscoveryBriefCreateSchema.partial().refine((value) => Object.keys(value).length > 0, { message: "At least one field must be supplied" });
// ─── Project working documents (finalised → Vault) ─────────
export const ProjectDocumentSchema = z.object({
    id: z.string().uuid(),
    projectId: z.string().uuid(),
    vaultDocumentId: z.string().uuid(),
    label: z.string().max(200).nullable(),
    role: z.string().max(80).nullable(),
    vaultDocumentTitle: z.string().nullable().optional(),
    createdBy: z.string().email(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const ProjectDocumentListResponseSchema = z.object({
    documents: z.array(ProjectDocumentSchema),
});
export const FinaliseProjectDocumentSchema = z.object({
    workspaceDocumentId: z.string().uuid(),
    vaultFolderId: z.string().uuid(),
    label: z.string().max(200).nullable().optional(),
    role: z.string().max(80).nullable().optional(),
});
// ─── Tasks ─────────────────────────────────────────────────
export const TaskPriorityEnum = z.enum(["low", "normal", "high", "critical"]);
/**
 * Task category. Every task has one — see ADR notes inline in
 * `packages/shared/src/constants/task-categories.ts`. The historical
 * backfill maps every pre-existing row to `product_development`, which is
 * also the create-time default when a caller omits it (the UI form still
 * requires an explicit pick).
 */
export const TaskCategoryEnum = z.enum(TASK_CATEGORY_SLUGS);
// `TaskCategory` (the runtime type) is exported from constants/task-categories.
export const TaskCreateSchema = z.object({
    projectId: z.string().uuid(),
    parentTaskId: z.string().uuid().nullable().optional(),
    title: z.string().min(1).max(300),
    description: z.string().max(10_000).optional(),
    status: z.string().min(1).max(30).default("todo"),
    priority: TaskPriorityEnum.default("normal"),
    // Defaults to `product_development` when omitted (mirrors the DB column
    // default) so existing API callers / seeds that predate categories keep
    // working — the create *form* still requires an explicit pick. Making this a
    // hard-required field instead 400s every task-create that doesn't send it.
    category: TaskCategoryEnum.default("product_development"),
    // Lead (the task's primary owner — the field historically called "assignee").
    assigneeEmail: z.string().email().nullable().optional(),
    // Support (the secondary person on the task). Same email-ref shape as lead.
    supportEmail: z.string().email().nullable().optional(),
    startDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .optional(),
    endDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .optional(),
    calendarStartsAt: z.string().datetime().nullable().optional(),
    calendarDurationMinutes: z.number().int().min(15).max(240).nullable().optional(),
    dependencies: z.array(z.string().uuid()).default([]),
    milestone: z.boolean().default(false),
    estimatedHours: z.number().nonnegative().nullable().optional(),
    actualHours: z.number().nonnegative().nullable().optional(),
    tags: z.array(z.string()).default([]),
    /** Shown map milestone node this overlay task mirrors (ADR-0111 live-sync). */
    mapNodeId: z.string().uuid().nullable().optional(),
});
export const TaskUpdateSchema = TaskCreateSchema.partial().omit({
    projectId: true,
});
export const TaskMoveSchema = z.object({
    status: z.string().min(1).max(30),
});
/**
 * Server-side filter for `GET /projects/:id/tasks`. Single-value query
 * param matches the existing filter shape on this route (no other
 * multi-value filters today); a comma-separated multi-value form can
 * follow when the project surface grows additional filters.
 */
export const TaskListQuerySchema = z.object({
    category: TaskCategoryEnum.optional(),
});
/**
 * Cross-project task aggregation for the dashboard's "Tasks by category"
 * widget. Returned by `GET /api/tasks/by-category`: a single round-trip
 * that delivers six counts (one per canonical category slug, even when
 * empty) plus a small "samples" list per category for the expanded
 * accordion view.
 *
 * Lanes are always present for all six categories — the dashboard
 * always renders six rows, with zero counts and empty sample arrays
 * for categories that have no open tasks today.
 */
export const TaskSampleSchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    projectId: z.string().uuid(),
    projectName: z.string(),
    assigneeEmail: z.string().email().nullable(),
});
/**
 * Per-category counts + sample slices. Keys are the canonical slugs;
 * record shape (rather than array) keeps lookups O(1) on the frontend
 * and makes "always show all six lanes" the type-level contract.
 */
export const TasksByCategoryResponseSchema = z.object({
    counts: z.record(TaskCategoryEnum, z.number().int().nonnegative()),
    samples: z.record(TaskCategoryEnum, z.array(TaskSampleSchema)),
});
export const TaskSchema = TaskCreateSchema.extend({
    id: z.string().uuid(),
    completedAt: z.string().datetime().nullable(),
    createdBy: z.string().email(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    calendarGraphEventId: z.string().nullable(),
    calendarOwnerEmail: z.string().max(320).nullable(),
    mapNodeId: z.string().uuid().nullable().optional(),
});
export const TaskCommentCreateSchema = z.object({
    body: z.string().trim().min(1).max(5000),
});
export const TaskCommentSchema = z.object({
    id: z.string().uuid(),
    projectId: z.string().uuid(),
    taskId: z.string().uuid(),
    body: z.string(),
    createdBy: z.string().email(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
// ─── Stakeholder updates ───────────────────────────────────
export const StakeholderUpdateCreateSchema = z.object({
    projectId: z.string().uuid(),
    title: z.string().min(1).max(300),
    bodyHtml: z.string().min(1),
    summary: z.string().max(2000).optional(),
    sentTo: z.array(z.string().email()).min(1).max(100),
});
export const StakeholderUpdateAIRequestSchema = z.object({
    projectId: z.string().uuid(),
    toneHint: z.string().max(200).optional(),
});
//# sourceMappingURL=projects.js.map